import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MultiSelect, MultiSelectOption } from "@/components/ui/multi-select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Plus } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { useOptimizedTasks } from "@/hooks/useOptimizedTasks";
import { useFamily } from "@/hooks/useFamily";
import { useTaskLists } from "@/hooks/useTaskLists";
import { useUniversalContacts } from "@/hooks/useUniversalContacts";
import { useToast } from "@/hooks/use-toast";
import { useCurrentSection } from "@/hooks/useCurrentSection";
import { PageHeader } from "@/components/PageHeader";
import { ConditionalWrapper } from "@/components/ConditionalWrapper";

export default function CreateTask() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { family } = useFamily();
  const { createTask, loading } = useOptimizedTasks(family?.id);
  const { contacts, getTaskAssignmentId } = useUniversalContacts(family?.id);
  const [searchParams] = useSearchParams();
  const preSelectedListId = searchParams.get('listId');
  const { taskLists, createTaskList } = useTaskLists(family?.id);
  const { primaryVariant } = useCurrentSection();
  const [listId, setListId] = useState(preSelectedListId || "");
  const [isCreating, setIsCreating] = useState(false);
  const [showNewListForm, setShowNewListForm] = useState(false);
  const [newListName, setNewListName] = useState("");
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assignedTo, setAssignedTo] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState<Date>();

  // Set default list if pre-selected
  useEffect(() => {
    if (preSelectedListId && taskLists.length > 0) {
      const selectedList = taskLists.find(list => list.id === preSelectedListId);
      if (selectedList) {
        setListId(preSelectedListId);
      }
    } else if (taskLists.length > 0 && !listId) {
      // Default to first list if none selected
      setListId(taskLists[0].id);
    }
  }, [preSelectedListId, taskLists, listId]);

  const handleCreateNewList = async () => {
    if (!newListName.trim() || !family?.id) return;
    
    try {
      const newList = await createTaskList(newListName.trim(), "📝");
      if (newList) {
        setListId(newList.id);
        setShowNewListForm(false);
        setNewListName("");
        toast({
          title: "Liste créée",
          description: `La liste "${newListName}" a été créée avec succès`,
        });
      }
    } catch (error) {
      console.error("Error creating task list:", error);
      toast({
        title: "Erreur",
        description: "Impossible de créer la liste",
        variant: "destructive",
      });
    }
  };

  const handleListChange = (value: string) => {
    if (value === "new") {
      setShowNewListForm(true);
      setListId("");
    } else {
      setListId(value);
      setShowNewListForm(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title || !listId) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Veuillez remplir tous les champs obligatoires.",
      });
      return;
    }

    console.log('💾 Creating task with prefixed IDs:', assignedTo);
    
    // Convert prefixed contact IDs (fm_xxx, pp_xxx) back to actual UUIDs for storage
    const actualIds = assignedTo
      .map((prefixedId) => {
        const contact = contacts.find(c => c.id === prefixedId);
        if (!contact) {
          console.warn(`⚠️ Contact not found for prefixed ID: ${prefixedId}`);
          return null;
        }
        
        const taskId = getTaskAssignmentId(contact);
        console.log(`🔄 Converting ${prefixedId} (${contact.displayName}) to UUID: ${taskId}`);
        return taskId;
      })
      .filter(Boolean) as string[];

    console.log('💾 Final UUIDs to save:', actualIds);
    
    await createTask(title, description, actualIds, dueDate, listId);
    navigate('/tasks');
  };

  const hasContacts = contacts.length > 0;


  const selectedList = taskLists.find(list => list.id === listId);

  return (
    <ConditionalWrapper
      type="page"
      skeletonType="form"
      variant="tasks"
      requiresFamily={true}
    >
      <div className="min-h-screen bg-page-gradient">
        <PageHeader 
          title={`Nouvelle tâche${selectedList ? ` · ${selectedList.name}` : ''}`}
          backPath="/tasks"
        />
        <div className="container mx-auto px-4 pt-2">
          <div className="max-w-md mx-auto">
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* List Selection */}
              <div className="space-y-2">
                <Label htmlFor="listId">Liste *</Label>
                <Select value={listId} onValueChange={handleListChange} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir une liste" />
                  </SelectTrigger>
                  <SelectContent>
                    {taskLists.map((list) => (
                      <SelectItem key={list.id} value={list.id}>
                        <div className="flex items-center space-x-2">
                          <span>{list.icon}</span>
                          <span>{list.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                    <SelectItem value="new">
                      <div className="flex items-center space-x-2 text-primary">
                        <Plus className="h-4 w-4" />
                        <span>Nouvelle liste</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* New List Form */}
              {showNewListForm && (
                <div className="space-y-2 p-4 border border-border rounded-lg bg-muted/20">
                  <Label htmlFor="newListName">Nom de la nouvelle liste</Label>
                  <div className="flex space-x-2">
                    <Input
                      id="newListName"
                      value={newListName}
                      onChange={(e) => setNewListName(e.target.value)}
                      placeholder="ex: Courses, Ménage..."
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      onClick={handleCreateNewList}
                      disabled={!newListName.trim()}
                      size="sm"
                    >
                      Créer
                    </Button>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="title">Titre *</Label>
                <Input
                  id="title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Que faut-il faire ?"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Détails supplémentaires (optionnel)"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="assignedTo">Assigner à *</Label>
                <MultiSelect
                  options={contacts.map((contact): MultiSelectOption => ({
                    value: contact.id,
                    label: contact.displayName,
                    avatar: contact.avatar_url,
                    group: contact.source === 'family_member' ? 'Famille' : 
                          contact.originalData.category === 'family' ? 'Famille' :
                          contact.originalData.category === 'friends' ? 'Amis' : 'Autre',
                  }))}
                  selected={assignedTo}
                  onChange={setAssignedTo}
                  placeholder="Choisir une ou plusieurs personnes"
                />
              </div>

              <div className="space-y-2">
                <Label>Échéance (optionnel)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      className={cn(
                        "w-full justify-start text-left font-normal border border-input hover:bg-accent hover:text-accent-foreground",
                        !dueDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dueDate ? format(dueDate, "PPP", { locale: fr }) : "Choisir une date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={dueDate}
                      onSelect={setDueDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </form>
          </div>
        </div>

        {/* Fixed action buttons */}
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t space-y-2">
          <Button
            variant="tasks"
            onClick={handleSubmit}
            className="w-full"
            disabled={!title.trim() || assignedTo.length === 0 || !listId || isCreating}
          >
            {isCreating ? "Création..." : "Créer la tâche"}
          </Button>
          <Button
            variant="cancel-hover"
            className="w-full"
            onClick={() => navigate("/tasks")}
          >
            Annuler
          </Button>
        </div>
      </div>
    </ConditionalWrapper>
  );
}